import { supabase } from '@/utils/supabase'

// Complete interface matching the fuel_records table schema exactly
export interface FuelRecord {
  // Primary key (auto-generated)
  id?: string; // UUID auto-generated
  
  // Vehicle information
  vehicle_id?: string | null; // UUID, can be null
  vehicle_reg?: string | null; // Text, can be null
  vehicle_name?: string | null; // Text, can be null
  
  // Required fields
  date: string; // Date, not null
  fuel_type: string; // Text, not null
  quantity: number; // Numeric(8,2), not null
  before_odometer_reading: number | null; // Numeric(12,2), can be null
  
  // Optional fields
  after_odometer_reading?: number | null; // Numeric, can be null
  location?: string | null; // Text, can be null
  filled_by?: string | null; // Text, can be null
  notes?: string | null; // Text, can be null
  cost_per_unit?: number | null; // Numeric(8,2), can be null
  total_cost?: number | null; // Numeric(10,2), can be null
  card_refuel?: boolean | null; // Boolean, can be null
  pump_id?: string | null; // UUID, can be null
  
  // Related entities
  driver_id?: string | null; // UUID, can be null
  driver_name?: string | null; // Text, can be null
  tank_id?: string | null; // UUID, can be null
  tank_name?: string | null; // Text, can be null
  depot_name?: string | null; // Text, can be null
  sub_contractor_id?: string | null; // UUID, can be null
  subcontractor_id?: string | null; // Alias for sub_contractor_id without underscore
  subconfleet_vehicle_id?: string | null; // UUID for subcontractor fleet vehicles, can be null
  
  // Image URLs
  odo_before_image_url?: string | null; // Text, can be null
  odo_after_image_url?: string | null; // Text, can be null
  voucher_photo_url?: string | null; // Text, can be null
  voucher_reference?: string | null; // Text, can be null
  
  // Timestamps (auto-generated)
  created_at?: string | null; // Timestamp with time zone, default now()
  updated_at?: string | null; // Timestamp with time zone, default now()
}

export const subcontractorRefuelService = {
  async createRefuel(fuelRecord: FuelRecord) {
    console.log('DETAILED DEBUG - Submitting to fuel_records:', JSON.stringify(fuelRecord, null, 2))
    
    try {
      // Skip schema cache refresh entirely - it's not needed and causes 404 errors
      console.log('Skipping schema refresh to avoid 404 errors')
      
      console.log('Inserting into table: fuel_records')
      
      // Create a record that exactly matches the database schema
      // Only include fields that are explicitly defined in the schema
      const recordToInsert = {
        // Required fields
        date: fuelRecord.date,
        fuel_type: fuelRecord.fuel_type,
        quantity: fuelRecord.quantity,
        before_odometer_reading: fuelRecord.before_odometer_reading,
        
        // Optional fields - only include if they have values
        vehicle_id: fuelRecord.vehicle_id || null,
        vehicle_reg: fuelRecord.vehicle_reg || null,
        vehicle_name: fuelRecord.vehicle_name || null,
        after_odometer_reading: fuelRecord.after_odometer_reading || null,
        location: fuelRecord.location || null,
        filled_by: fuelRecord.filled_by || null,
        notes: fuelRecord.notes || null,
        cost_per_unit: fuelRecord.cost_per_unit || null,
        total_cost: fuelRecord.total_cost || null,
        card_refuel: fuelRecord.card_refuel || null,
        driver_id: fuelRecord.driver_id || null,
        driver_name: fuelRecord.driver_name || null,
        tank_id: fuelRecord.tank_id || null,
        tank_name: fuelRecord.tank_name || null,
        depot_name: fuelRecord.depot_name || null,
        
        // Use the correct column name that matches database schema
        sub_contractor_id: fuelRecord.subcontractor_id || null,
        
        // Include the subcontractor fleet vehicle ID in the dedicated field
        subconfleet_vehicle_id: fuelRecord.subconfleet_vehicle_id || null,
        
        // Explicitly handle pump_id to ensure it's passed correctly
        pump_id: fuelRecord.pump_id || null,
        
        // Add pump reading fields to satisfy the valid_pump_readings constraint
        pump_read_before: fuelRecord.before_odometer_reading || null,
        pump_read_after: fuelRecord.after_odometer_reading || null,
        
        odo_before_image_url: fuelRecord.odo_before_image_url || null,
        odo_after_image_url: fuelRecord.odo_after_image_url || null,
        voucher_photo_url: fuelRecord.voucher_photo_url || null,
        voucher_reference: fuelRecord.voucher_reference || null
      }
      
      console.log('RECORD FOR INSERTION:', JSON.stringify(recordToInsert, null, 2))
      
      // Add detailed pump_id debugging
      console.log('PUMP ID DETAILS:', {
        original: fuelRecord.pump_id,
        originalType: typeof fuelRecord.pump_id,
        final: recordToInsert.pump_id,
        finalType: typeof recordToInsert.pump_id
      })
      
      // Ensure pump reading constraint is satisfied if pump_id is provided
      if (recordToInsert.pump_id && 
          (recordToInsert.pump_read_before === null || recordToInsert.pump_read_before === undefined ||
           recordToInsert.pump_read_after === null || recordToInsert.pump_read_after === undefined ||
           recordToInsert.pump_read_after <= recordToInsert.pump_read_before)) {
        
        if (recordToInsert.pump_read_before === null || recordToInsert.pump_read_before === undefined) {
          recordToInsert.pump_read_before = recordToInsert.before_odometer_reading || 0
        }
        
        if (recordToInsert.pump_read_after === null || recordToInsert.pump_read_after === undefined) {
          recordToInsert.pump_read_after = recordToInsert.after_odometer_reading || 
            (recordToInsert.pump_read_before + recordToInsert.quantity)
        }
        
        // Ensure pump_read_after > pump_read_before
        if (recordToInsert.pump_read_after <= recordToInsert.pump_read_before) {
          recordToInsert.pump_read_after = recordToInsert.pump_read_before + recordToInsert.quantity
        }
      }
      
      // Make sure required fields have values
      // If before_odometer_reading is null, set it to 0 (not null constraint)
      if (recordToInsert.before_odometer_reading === null || recordToInsert.before_odometer_reading === undefined) {
        recordToInsert.before_odometer_reading = 0
      }
      
      // If after_odometer_reading is null, make it slightly higher than before_odometer_reading
      if (recordToInsert.after_odometer_reading === null || recordToInsert.after_odometer_reading === undefined) {
        recordToInsert.after_odometer_reading = recordToInsert.before_odometer_reading + recordToInsert.quantity
      }
      
      // Strategy 3: If quantity is the issue, set it to a small positive value
      if (recordToInsert.quantity <= 0) {
        recordToInsert.quantity = 0.001
      }
      
      // Strategy 4: Keep all fields as provided
      
      // Log the final record state
      console.log('FINAL RECORD STATE AFTER MULTIPLE STRATEGIES:', {
        before_reading: recordToInsert.before_odometer_reading,
        after_reading: recordToInsert.after_odometer_reading,
        pump_read_before: recordToInsert.pump_read_before,
        pump_read_after: recordToInsert.pump_read_after,
        quantity: recordToInsert.quantity,
        pump_id: recordToInsert.pump_id,
        card_refuel: (recordToInsert as any).card_refuel
      })
      
      // Final validation to ensure all required fields have values
      const requiredFields = ['date', 'fuel_type', 'quantity', 'before_odometer_reading']
      for (const field of requiredFields) {
        if (recordToInsert[field as keyof typeof recordToInsert] === null || 
            recordToInsert[field as keyof typeof recordToInsert] === undefined) {
          console.error(`Missing required field: ${field}`)
          throw new Error(`Required field ${field} is missing. Please check your form data.`)
        }
      }
      
      // Ensure after_odometer_reading is greater than before_odometer_reading
      if (recordToInsert.after_odometer_reading !== null && 
          recordToInsert.before_odometer_reading !== null && 
          recordToInsert.after_odometer_reading <= recordToInsert.before_odometer_reading) {
        console.log('Fixing after_odometer_reading to be greater than before_odometer_reading')
        recordToInsert.after_odometer_reading = recordToInsert.before_odometer_reading + recordToInsert.quantity
      }
      
      // Skip schema details check to avoid 404 errors
      console.log('Skipping schema details check to avoid 404 errors')
      
      // Skip the RPC call since it's causing issues and go directly to a regular insert
      console.log('USING DIRECT INSERT INSTEAD OF RPC CALL')
      
      // Use direct insert with the prepared record
      const { data, error } = await supabase
        .from('fuel_records')
        .insert([recordToInsert])
        .select()
        .single()
      
      if (error) {
        console.error('DETAILED ERROR from Supabase:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        
        // Log the payload that caused the error
        console.error('Payload that caused error:', JSON.stringify(fuelRecord, null, 2))
        
        // Try to identify if there's a field name mismatch
        const errorMsg = error.message || ''
        if (errorMsg.includes('column') && errorMsg.includes('does not exist')) {
          const matches = errorMsg.match(/column ['"](.+?)['"] does not exist/)
          if (matches && matches[1]) {
            const badColumn = matches[1]
            console.error(`Bad column name detected: ${badColumn}`)
            console.error('Available fields in record:', Object.keys(fuelRecord).join(', '))
            
            // Create a more helpful error message
            const enhancedError = new Error(`Database column '${badColumn}' does not exist. This may be due to a schema mismatch. Please check field names and ensure all migrations have been applied.`)
            enhancedError.name = 'SchemaError'
            throw enhancedError
          }
        }
        
        // Handle constraint violations
        if (errorMsg.includes('violates') && errorMsg.includes('constraint')) {
          const constraintMatch = errorMsg.match(/violates (.+?) constraint/)
          if (constraintMatch && constraintMatch[1]) {
            const constraintType = constraintMatch[1]
            console.error(`Constraint violation: ${constraintType}`)
            
            // Check for not-null constraint specifically
            if (constraintType === 'not-null') {
              // Try to extract the column name from the error message
              const columnMatch = errorMsg.match(/column "([^"]+)" of relation/)
              const columnName = columnMatch ? columnMatch[1] : 'unknown'
              console.error(`Not-null constraint violation on column: ${columnName}`)
              console.error(`Value provided for ${columnName}:`, recordToInsert[columnName as keyof typeof recordToInsert])
              
              // Create a more helpful error message with the column name
              const enhancedError = new Error(`Database not-null constraint violation on column '${columnName}'. Please provide a value for this field.`)
              enhancedError.name = 'NotNullConstraintViolationError'
              throw enhancedError
            } else {
              // Create a more helpful error message for other constraint types
              const enhancedError = new Error(`Database constraint violation: ${constraintType}. Please check the data being submitted.`)
              enhancedError.name = 'ConstraintViolationError'
              throw enhancedError
            }
          }
        }
        
        throw error
      }
      
      console.log('DETAILED SUCCESS - Record created:', JSON.stringify(data, null, 2))
      return { data, error: null }
    } catch (err) {
      console.error('DETAILED EXCEPTION:', err)
      throw err
    }
  }
}
